/**
 * Campaign tracking event hook
 * Created: 2026-03-07 - Returns trackEvent function for funnel step tracking
 * Updated: 2026-03-16 - Generate visitor_id on the fly if missing; add URL + sessionStorage fallback for attribution
 */

const EDGE_URL = "https://sdvakfhmoaoucmhbhwvy.supabase.co/functions/v1/track-event";
const STORAGE_KEY = "tracking_attribution";
const VISITOR_KEY = "tracking_visitor_id";
const ATTRIBUTION_TTL_DAYS = 30;

interface TrackEventOptions {
  event_type:
    | "valuation_started"
    | "valuation_completed"
    | "registration"
    | "listing_submitted";
  user_id?: string;
  metadata?: Record<string, unknown>;
}

function getAttribution(): { code: string } | null {
  try {
    // 1. Check localStorage (primary)
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const data = JSON.parse(raw);
      const age = Date.now() - data.captured_at;
      if (age > ATTRIBUTION_TTL_DAYS * 24 * 60 * 60 * 1000) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        return { code: data.code };
      }
    }

    // 2. Check sessionStorage (secondary backup)
    const sessionRef = sessionStorage.getItem("tracking_ref_code");
    if (sessionRef) {
      return { code: sessionRef };
    }

    // 3. Check current URL params (last resort)
    const urlRef = new URLSearchParams(window.location.search).get("ref");
    if (urlRef) {
      return { code: urlRef };
    }

    return null;
  } catch {
    return null;
  }
}

export function useTrackEvent() {
  return (options: TrackEventOptions) => {
    // Generate visitor_id on the fly if missing — never silently drop events
    let visitorId = localStorage.getItem(VISITOR_KEY);
    if (!visitorId) {
      visitorId = crypto.randomUUID();
      localStorage.setItem(VISITOR_KEY, visitorId);
    }

    const attribution = getAttribution();
    const sessionId = sessionStorage.getItem("tracking_session_id");

    fetch(EDGE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code: attribution?.code || null,
        event_type: options.event_type,
        visitor_id: visitorId,
        session_id: sessionId,
        user_id: options.user_id || null,
        page_url: window.location.href,
        metadata: options.metadata || {},
      }),
    }).catch(() => {}); // Fire-and-forget
  };
}
